import { DESIGN_HEIGHT, DESIGN_WIDTH, poseAt } from "./motion";
import { FRAGMENT_SHADER, VERTEX_SHADER } from "./shaders";

// The picture is soft, so rendering beyond this pixel ratio costs GPU time without a visible gain.
const MAX_PIXEL_RATIO = 1.5;

const UNIFORMS = [ "u_height", "u_cover", "u_lobeA", "u_lobeB", "u_squircle", "u_leftCorner" ];

/**
 * Draws the login background, a gradient with a blob and a glass squircle, onto a canvas with WebGL.
 */
export default class LoginBackgroundRenderer {
	/**
	 * Creates a renderer for the canvas.
	 *
	 * @param {HTMLCanvasElement} canvas The canvas to draw on.
	 *
	 * @returns {LoginBackgroundRenderer|null} The renderer, or null when the browser cannot render it.
	 */
	static create( canvas ) {
		const gl = canvas.getContext( "webgl", {
			alpha: false,
			antialias: false,
			depth: false,
			stencil: false,
			powerPreference: "low-power",
		} );

		if ( ! gl ) {
			return null;
		}

		const renderer = new LoginBackgroundRenderer( canvas, gl );

		if ( ! renderer.setUp() ) {
			renderer.destroy();
			return null;
		}

		return renderer;
	}

	/**
	 * @param {HTMLCanvasElement}     canvas The canvas to draw on.
	 * @param {WebGLRenderingContext} gl     The canvas's WebGL context.
	 */
	constructor( canvas, gl ) {
		this.canvas = canvas;
		this.gl = gl;
		this.program = null;
		this.uniforms = {};
		this.time = 0;
		this.onContextLost = this.onContextLost.bind( this );
		this.onContextRestored = this.onContextRestored.bind( this );

		canvas.addEventListener( "webglcontextlost", this.onContextLost );
		canvas.addEventListener( "webglcontextrestored", this.onContextRestored );
	}

	/**
	 * Compiles the shaders and uploads the triangle that covers the viewport.
	 *
	 * @returns {boolean} Whether the renderer is ready to draw.
	 */
	setUp() {
		const gl = this.gl;
		const program = gl.createProgram();

		gl.attachShader( program, this.compile( gl.VERTEX_SHADER, VERTEX_SHADER ) );
		gl.attachShader( program, this.compile( gl.FRAGMENT_SHADER, FRAGMENT_SHADER ) );
		gl.linkProgram( program );

		if ( ! gl.getProgramParameter( program, gl.LINK_STATUS ) ) {
			return false;
		}

		gl.useProgram( program );
		gl.bindBuffer( gl.ARRAY_BUFFER, gl.createBuffer() );
		gl.bufferData( gl.ARRAY_BUFFER, new Float32Array( [ -1, -1, 3, -1, -1, 3 ] ), gl.STATIC_DRAW );

		const position = gl.getAttribLocation( program, "a_position" );
		gl.enableVertexAttribArray( position );
		gl.vertexAttribPointer( position, 2, gl.FLOAT, false, 0, 0 );

		this.program = program;
		this.uniforms = Object.fromEntries( UNIFORMS.map( name => [ name, gl.getUniformLocation( program, name ) ] ) );
		this.resize();

		return true;
	}

	/**
	 * @param {number} type   The shader type.
	 * @param {string} source The GLSL source.
	 *
	 * @returns {WebGLShader} The compiled shader; a failure surfaces when the program links.
	 */
	compile( type, source ) {
		const shader = this.gl.createShader( type );

		this.gl.shaderSource( shader, source );
		this.gl.compileShader( shader );

		return shader;
	}

	/**
	 * Matches the drawing buffer to the canvas's displayed size and covers it with the design space.
	 */
	resize() {
		const gl = this.gl;
		const ratio = Math.min( window.devicePixelRatio || 1, MAX_PIXEL_RATIO );
		const width = Math.max( 1, Math.round( this.canvas.clientWidth * ratio ) );
		const height = Math.max( 1, Math.round( this.canvas.clientHeight * ratio ) );

		this.canvas.width = width;
		this.canvas.height = height;

		if ( ! this.program ) {
			return;
		}

		// Like `object-fit: cover`: scale the design space to fill the buffer and centre the overflow.
		const designPerPixel = Math.min( DESIGN_WIDTH / width, DESIGN_HEIGHT / height );

		gl.viewport( 0, 0, width, height );
		gl.uniform1f( this.uniforms.u_height, height );
		gl.uniform3f(
			this.uniforms.u_cover,
			designPerPixel,
			( DESIGN_WIDTH - width * designPerPixel ) / 2,
			( DESIGN_HEIGHT - height * designPerPixel ) / 2,
		);
		this.render( this.time );
	}

	/**
	 * Draws the background as it looks at a point in time.
	 *
	 * @param {number} time The time in ms.
	 */
	render( time ) {
		this.time = time;

		if ( ! this.program || this.gl.isContextLost() ) {
			return;
		}

		const gl = this.gl;
		const pose = poseAt( time );

		gl.uniform3fv( this.uniforms.u_lobeA, pose.lobeA );
		gl.uniform3fv( this.uniforms.u_lobeB, pose.lobeB );
		gl.uniform4fv( this.uniforms.u_squircle, pose.squircle.slice( 0, 4 ) );
		gl.uniform1f( this.uniforms.u_leftCorner, pose.squircle[ 4 ] );
		gl.drawArrays( gl.TRIANGLES, 0, 3 );
	}

	/**
	 * @param {Event} event The context lost event.
	 */
	onContextLost( event ) {
		// Without this the browser never restores the context.
		event.preventDefault();
		this.program = null;
	}

	/**
	 * Rebuilds the GPU resources, which the lost context took with it.
	 */
	onContextRestored() {
		this.setUp();
	}

	/**
	 * Stops listening to the canvas.
	 *
	 * The context is left alive: a canvas has one context for life, and a remount (as in Strict Mode) reuses it.
	 */
	destroy() {
		this.canvas.removeEventListener( "webglcontextlost", this.onContextLost );
		this.canvas.removeEventListener( "webglcontextrestored", this.onContextRestored );
	}
}
